import type { HtmlLinkDescriptor } from 'react-router';

/**
 * Responsive descriptors for the full-viewport hero photos. The WebP variants
 * are generated by `bun run images:hero` (scripts/generate-hero-images.ts) into
 * `public/images/`; keep the widths below in sync with that script.
 *
 * Defined once so the rendered `<picture>` (HeroBackground) and the route
 * preload links read the same srcset/sizes/media and cannot drift apart.
 */

/** Landscape (default) variant set, rendered by the `<img>`. */
export interface HeroLandscapeImage {
  /** Fallback src, the 1280px variant. */
  src: string;
  srcSet: string;
  sizes: string;
}

/** Art-directed portrait crop, rendered by a `<source>` below the md breakpoint. */
export interface HeroPortraitImage {
  srcSet: string;
  sizes: string;
  media: string;
}

export interface HeroImage {
  landscape: HeroLandscapeImage;
  portrait?: HeroPortraitImage;
}

/** Matches Tailwind's md breakpoint (768px): portrait applies below it. */
export const HERO_PORTRAIT_MEDIA = '(max-width: 767px)';

export const HOME_HERO_IMAGE: HeroImage = {
  landscape: {
    src: '/images/hero-home-1280.webp',
    srcSet:
      '/images/hero-home-768.webp 768w, /images/hero-home-1280.webp 1280w, /images/hero-home-1920.webp 1920w, /images/hero-home-2560.webp 2560w',
    sizes: '100vw',
  },
  portrait: {
    srcSet: '/images/hero-home-portrait-480.webp 480w, /images/hero-home-portrait-768.webp 768w',
    sizes: '100vw',
    media: HERO_PORTRAIT_MEDIA,
  },
};

export const SUCCESS_HERO_IMAGE: HeroImage = {
  landscape: {
    src: '/images/hero-success-1280.webp',
    srcSet:
      '/images/hero-success-768.webp 768w, /images/hero-success-1280.webp 1280w, /images/hero-success-1920.webp 1920w, /images/hero-success-2560.webp 2560w',
    sizes: '100vw',
  },
  portrait: {
    srcSet:
      '/images/hero-success-portrait-480.webp 480w, /images/hero-success-portrait-768.webp 768w',
    sizes: '100vw',
    media: HERO_PORTRAIT_MEDIA,
  },
};

/**
 * Image preload `<link>` descriptors for a hero, one per orientation, so the
 * browser fetches only the variant the `<picture>` will render. With a portrait
 * crop, the portrait link carries its media query and the landscape link the
 * exact negation of it (`not all and ...`), mirroring the `<picture>`
 * fall-through with no gap at fractional widths; otherwise the landscape link
 * is ungated. No `fetchPriority`: React Router's LinkDescriptor does not type
 * it, and a background preload should not compete with the current page.
 */
export const buildHeroPreloadLinks = ({ landscape, portrait }: HeroImage): HtmlLinkDescriptor[] => {
  const landscapeLink: HtmlLinkDescriptor = {
    rel: 'preload',
    as: 'image',
    imageSrcSet: landscape.srcSet,
    imageSizes: landscape.sizes,
  };

  if (!portrait) {
    return [landscapeLink];
  }

  return [
    {
      rel: 'preload',
      as: 'image',
      media: portrait.media,
      imageSrcSet: portrait.srcSet,
      imageSizes: portrait.sizes,
    },
    { ...landscapeLink, media: `not all and ${portrait.media}` },
  ];
};
